import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../lib/auth-context";
import { SecurityBadge } from "./SecurityBadge";
import Mfa2FAReminder from "./Mfa2FAReminder";
import SubscriptionExpiryBanner from "./SubscriptionExpiryBanner";
import LanguageSwitcher from "./LanguageSwitcher";
import {
  House, Bed, GraduationCap, Student, Books,
  HandHeart, Certificate, Receipt, ChartLineUp, UsersThree,
  ShieldCheck, SignOut, ListBullets, Buildings, EnvelopeOpen, ShieldStar,
  ChartBar, Money, Scales, Calendar, Database, Envelope, Gear, HeartHalf, Users, CreditCard,
  ClipboardText, Truck, BookOpen, WhatsappLogo, ForkKnife, FirstAidKit, Warning, QrCode,
  Question,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";

const ROLE_NAV = {
  super_admin: [
    { to: "/app", label: "Overview", icon: ChartLineUp, end: true },
    { to: "/app/students", label: "Students", icon: Student },
    { to: "/app/admissions", label: "Admissions", icon: ListBullets },
    { to: "/app/hostels", label: "Hostels", icon: Bed },
    { to: "/app/schools", label: "Schools", icon: GraduationCap },
    { to: "/app/users", label: "Users", icon: UsersThree },
    { to: "/app/nominate-donor", label: "Nominate Donor", icon: HandHeart },
    { to: "/app/certificates", label: "Certificates", icon: Certificate },
    { to: "/app/approvals", label: "Approvals", icon: ShieldCheck },
    { to: "/app/alumni", label: "Alumni", icon: GraduationCap },
    { to: "/app/audit", label: "Audit Logs", icon: ShieldCheck },
    { to: "/app/reports", label: "Reports", icon: ChartBar },
    { to: "/app/expenditure", label: "Expenditure", icon: Money },
    { to: "/app/admin-donations", label: "Donations", icon: HandHeart },
    { to: "/app/rbac", label: "Roles & Perms", icon: Scales },
    { to: "/app/trusts", label: "Trusts", icon: Buildings },
    { to: "/app/super/audit", label: "Tenant Audit", icon: ShieldCheck },
    { to: "/app/subscriptions", label: "Subscriptions", icon: Receipt },
    { to: "/app/onboard", label: "Onboard Trust", icon: Buildings },
    { to: "/app/master-data", label: "Master Data", icon: Database },
    { to: "/app/attendance", label: "Attendance", icon: ClipboardText },
    { to: "/app/transport", label: "Transport", icon: Truck },
    { to: "/app/library", label: "Library", icon: BookOpen },
    { to: "/app/mess-menu", label: "Mess Menu", icon: ForkKnife },
    { to: "/app/mess-attendance", label: "Mess Attendance", icon: ForkKnife },
    { to: "/app/mess-scan", label: "Mess Scan", icon: QrCode },
    { to: "/app/health", label: "Health", icon: FirstAidKit },
    { to: "/app/fee-escalation", label: "Fee Escalation", icon: Warning },
    { to: "/app/whatsapp", label: "WhatsApp", icon: WhatsappLogo },
    { to: "/app/notifications", label: "Notifications", icon: EnvelopeOpen },
    { to: "/app/settings", label: "Settings", icon: Gear },
    { to: "/app/subscriptions", label: "Subscriptions", icon: CreditCard },
    { to: "/app/backup", label: "Data Backup", icon: Database },
    { to: "/app/sac", label: "Support a Child", icon: HeartHalf },
    { to: "/app/sac/incomplete", label: "SAC Incomplete", icon: ShieldCheck },
    { to: "/app/messages", label: "Messages", icon: Envelope },
    { to: "/app/security", label: "Security (2FA)", icon: ShieldStar },
  ],
  hostel_admin: [
    { to: "/app", label: "Occupancy", icon: ChartLineUp, end: true },
    { to: "/app/students", label: "Students", icon: Student },
    { to: "/app/admissions", label: "Admissions", icon: ListBullets },
    { to: "/app/fees", label: "Fees", icon: Receipt },
    { to: "/app/progress", label: "Progress", icon: ChartLineUp },
    { to: "/app/attendance", label: "Attendance", icon: ClipboardText },
    { to: "/app/transport", label: "Transport", icon: Truck },
    { to: "/app/mess-menu", label: "Mess Menu", icon: ForkKnife },
    { to: "/app/mess-attendance", label: "Mess Attendance", icon: ForkKnife },
    { to: "/app/mess-scan", label: "Mess Scan", icon: QrCode },
    { to: "/app/health", label: "Health", icon: FirstAidKit },
    { to: "/app/expenditure", label: "Expenditure", icon: Money },
    { to: "/app/admin-donations", label: "Donations", icon: HandHeart },
    { to: "/app/reports", label: "Reports", icon: ChartBar },
    { to: "/app/certificates", label: "Certificates", icon: Certificate },
    { to: "/app/nominate-donor", label: "Nominate Donor", icon: HandHeart },
    { to: "/app/approvals", label: "My Approvals", icon: ShieldCheck },
    { to: "/app/alumni", label: "Alumni", icon: GraduationCap },
    { to: "/app/security", label: "Security (2FA)", icon: ShieldStar },
  ],
  sac_admin: [
    { to: "/app", label: "SAC home", icon: HeartHalf, end: true },
    { to: "/app/sac", label: "All SAC profiles", icon: Users },
    { to: "/app/sac/incomplete", label: "Pending corrections", icon: ShieldCheck },
    { to: "/app/security", label: "Security (2FA)", icon: ShieldStar },
  ],
  trust_admin: [
    { to: "/app", label: "Trust overview", icon: Buildings, end: true },
    { to: "/app/hostels", label: "Hostels", icon: Bed },
    { to: "/app/schools", label: "Schools", icon: GraduationCap },
    { to: "/app/students", label: "Students", icon: Student },
    { to: "/app/reports", label: "Reports", icon: ChartBar },
    { to: "/app/reports/gender", label: "Gender Report", icon: ChartBar },
    { to: "/app/audit", label: "Audit Logs", icon: ShieldCheck },
    { to: "/app/admin-donations", label: "Donations", icon: HandHeart },
    { to: "/app/expenditure", label: "Expenditure", icon: Money },
    { to: "/app/security", label: "Security (2FA)", icon: ShieldStar },
  ],
  school_admin: [
    { to: "/app", label: "Overview", icon: ChartLineUp, end: true },
    { to: "/app/students", label: "Students", icon: Student },
    { to: "/app/admissions", label: "Admissions", icon: ListBullets },
    { to: "/app/fees", label: "Fees", icon: Receipt },
    { to: "/app/marksheets", label: "Marksheets", icon: Books },
    { to: "/app/attendance", label: "Attendance", icon: ClipboardText },
    { to: "/app/transport", label: "Transport", icon: Truck },
    { to: "/app/library", label: "Library", icon: BookOpen },
    { to: "/app/health", label: "Health", icon: FirstAidKit },
    { to: "/app/expenditure", label: "Expenditure", icon: Money },
    { to: "/app/admin-donations", label: "Donations", icon: HandHeart },
    { to: "/app/reports", label: "Reports", icon: ChartBar },
    { to: "/app/certificates", label: "Certificates", icon: Certificate },
    { to: "/app/nominate-donor", label: "Nominate Donor", icon: HandHeart },
    { to: "/app/approvals", label: "My Approvals", icon: ShieldCheck },
    { to: "/app/alumni", label: "Alumni", icon: GraduationCap },
    { to: "/app/security", label: "Security (2FA)", icon: ShieldStar },
  ],
  teacher: [
    { to: "/app", label: "My Subject", icon: Books, end: true },
    { to: "/app/teacher/marks", label: "Marks Entry", icon: Books },
    { to: "/app/marksheets", label: "Marksheets", icon: Books },
    { to: "/app/attendance", label: "Attendance", icon: ClipboardText },
    { to: "/app/library", label: "Library", icon: BookOpen },
    { to: "/app/students", label: "Students", icon: Student },
    { to: "/app/messages", label: "Messages", icon: Envelope },
    { to: "/app/security", label: "Security (2FA)", icon: ShieldStar },
  ],
  donor: [
    { to: "/app", label: "My Students", icon: HandHeart, end: true },
    { to: "/app/donor-portfolio", label: "Portfolio", icon: Books },
    { to: "/app/feed", label: "Activity Feed", icon: ChartLineUp },
    { to: "/app/donations", label: "Donations", icon: Receipt },
    { to: "/app/messages", label: "Messages", icon: Envelope },
    { to: "/app/security", label: "Security (2FA)", icon: ShieldStar },
  ],
  parent: [
    { to: "/app", label: "My Family", icon: Student, end: true },
    { to: "/app/messages", label: "Messages", icon: Envelope },
    { to: "/app/security", label: "Security (2FA)", icon: ShieldStar },
  ],
};

function RoleTag({ role }) {
  const map = {
    super_admin: "Super Admin",
    hostel_admin: "Hostel Admin",
    school_admin: "School Admin",
    teacher: "Teacher",
    donor: "Donor",
  };
  return (
    <span
      data-testid="role-tag"
      className="font-mono text-[10px] tracking-widest uppercase px-2 py-1 rounded-md border border-[var(--vs-border)] bg-[var(--vs-bg-subtle)] text-[var(--vs-secondary)]"
    >
      {map[role] || role}
    </span>
  );
}

function BrandHeader({ brandLogo, brandLabel, scope }) {
  return (
    <div className="px-5 py-6 flex items-center gap-2 border-b border-[var(--vs-border)]">
      {brandLogo ? (
        <img src={brandLogo} alt={brandLabel} className="w-9 h-9 rounded-md object-cover bg-[var(--vs-bg-subtle)]" data-testid="org-logo" />
      ) : (
        <div className="w-9 h-9 rounded-md bg-[var(--vs-secondary)] text-[var(--vs-bg)] grid place-items-center font-display font-bold" data-testid="org-logo-fallback">
          {brandLabel.slice(0, 2).toUpperCase()}
        </div>
      )}
      <div className="min-w-0">
        <div className="font-display font-semibold leading-tight truncate" data-testid="org-label" title={brandLabel}>{brandLabel}</div>
        <div className="text-[11px] text-[var(--vs-text-secondary)] tracking-wide">
          {scope ? `${scope[0].toUpperCase()}${scope.slice(1)} workspace` : "Management System"}
        </div>
      </div>
    </div>
  );
}

// Maps the english label in ROLE_NAV to the i18n key used in /app/frontend/src/i18n/locales/*.json.
// Untranslated entries fall back to their english label.
const NAV_TKEY = {
  "Overview": "nav.overview",
  "Students": "nav.students",
  "Admissions": "nav.admissions",
  "Hostels": "nav.hostels",
  "Schools": "nav.schools",
  "Users": "nav.users",
  "Donors": "nav.donors",
  "Nominate Donor": "nav.nominate_donor",
  "Certificates": "nav.certificates",
  "Approvals": "nav.approvals",
  "Alumni": "nav.alumni",
  "Audit Logs": "nav.audit_logs",
  "Reports": "nav.reports",
  "Expenditure": "nav.expenditure",
  "Donations": "nav.donations",
  "Admin Donations": "nav.donations",
  "Roles & Perms": "nav.roles_perms",
  "Trusts": "nav.trusts",
  "Tenant Audit": "nav.tenant_audit",
  "Onboard Trust": "nav.onboard_trust",
  "Master Data": "nav.master_data",
  "Attendance": "nav.attendance",
  "Transport": "nav.transport",
  "Library": "nav.library",
  "WhatsApp": "nav.whatsapp",
  "Mess Menu": "nav.mess_menu",
  "Mess Scan": "nav.mess_scan",
  "Health": "nav.health",
  "Fee Escalation": "nav.fee_escalation",
  "Mess Attendance": "nav.attendance",
  "Fees": "nav.fees",
  "Progress": "nav.progress",
  "Marksheets": "nav.marksheets",
  "Photos": "nav.photos",
  "Messages": "nav.messages",
  "Settings": "nav.settings",
  "Security": "nav.security",
  "Data Backup": "nav.backup",
  "Notifications": "nav.notifications",
  "Subscriptions": "nav.subscriptions",
  "SAC": "nav.sac",
  "Sign out": "nav.sign_out",
};

function SidebarNav({ items }) {
  const { t } = useTranslation();
  return (
    <nav className="flex-1 p-3 space-y-1">
      {items.map((it) => {
        const Icon = it.icon;
        const tKey = NAV_TKEY[it.label];
        const label = tKey ? t(tKey, it.label) : it.label;
        return (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                isActive
                  ? "bg-[var(--vs-bg-subtle)] text-[var(--vs-text)] font-medium"
                  : "text-[var(--vs-text-secondary)] hover:bg-[var(--vs-bg-subtle)] hover:text-[var(--vs-text)]"
              }`
            }
          >
            <Icon size={18} weight="regular" />
            <span>{label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}

function TopBar({ user, title, subtitle }) {
  return (
    <header className="border-b border-[var(--vs-border)] bg-white">
      <div className="px-6 py-4 flex items-center justify-between">
        <div>
          <div className="eyebrow">{user?.role?.replace("_", " ")} workspace</div>
          <h1 data-testid="page-title" className="font-display text-2xl md:text-3xl font-semibold tracking-tight">
            {title}
          </h1>
          {subtitle && <div className="text-sm text-[var(--vs-text-secondary)] mt-0.5">{subtitle}</div>}
        </div>
        <div className="flex items-center gap-3">
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/docs/user-training.pdf`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="topbar-training-manual"
            className="inline-flex items-center gap-1.5 text-xs text-[var(--vs-text-secondary)] hover:text-[var(--vs-secondary)] transition-colors font-mono uppercase tracking-widest"
            title="Download the VSMS User Training Manual (PDF)"
          >
            <Question size={14} weight="bold" /> Help
          </a>
          <LanguageSwitcher compact />
          <RoleTag role={user?.role} />
          <div className="text-right">
            <div className="text-sm font-medium">{user?.name}</div>
            <div className="text-xs text-[var(--vs-text-secondary)]">{user?.email}</div>
          </div>
          <div className="w-9 h-9 rounded-full bg-[var(--vs-primary)] text-[var(--vs-text)] grid place-items-center font-display font-semibold">
            {user?.name?.charAt(0)}
          </div>
        </div>
      </div>
    </header>
  );
}

export default function DashboardLayout({ children, title, subtitle }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const items = ROLE_NAV[user?.role] || [];
  const [branding, setBranding] = React.useState(null);

  React.useEffect(() => {
    if (!user) return;
    api.get("/branding").then((r) => setBranding(r.data)).catch(() => {});
  }, [user?.id]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const brandLogo = branding?.logo_path
    ? `${apiBase}/api/files/logo?path=${encodeURIComponent(branding.logo_path)}`
    : null;
  const brandLabel = branding?.label || "Vidya Seva";

  return (
    <div className="min-h-screen flex bg-[var(--vs-bg)]">
      <aside data-testid="sidebar" className="hidden md:flex md:w-64 flex-col border-r border-[var(--vs-border)] bg-white">
        <BrandHeader brandLogo={brandLogo} brandLabel={brandLabel} scope={branding?.scope} />
        <SidebarNav items={items} />
        <div className="p-3 border-t border-[var(--vs-border)] space-y-3">
          <SecurityBadge compact />
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-[var(--vs-border)] hover:bg-[var(--vs-bg-subtle)] transition-colors"
          >
            <SignOut size={16} />
            {t("nav.sign_out")}
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <TopBar user={user} title={title} subtitle={subtitle} />
        <main className="flex-1 p-6">
          <SubscriptionExpiryBanner />
          <Mfa2FAReminder />
          {children}
        </main>
      </div>
    </div>
  );
}
